import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Container, Grid, Typography, Box } from "@mui/material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { STATUS } from "../../../../../redux/constants";
import { resetAddCombustionStatus } from "../../../../../redux/actions";
import useStyles from "./styles";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CeroItemPair from "../../../../../components/CeroItemPair";

const UpstreamLeasedAsset = (props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { emissionData, onCancel } = props;

  const deleteEmissionData = useSelector(
    (state) => state.emission.deleteEmissions
  );

  useEffect(() => {
    if (deleteEmissionData.status === STATUS.SUCCESS) {
      enqueueSnackbar("Upstream leased asset record deleted successfully", {
        variant: "success",
      });
      dispatch(resetAddCombustionStatus());
      onCancel();
    } else if (deleteEmissionData.status === STATUS.ERROR) {
      enqueueSnackbar(
        deleteEmissionData.message.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(resetAddCombustionStatus());
    }
  }, [deleteEmissionData, enqueueSnackbar, onCancel, dispatch]);

  const monthDict = {
    1: "January",
    2: "February",
    3: "March",
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
  };

  return (
    <>
      <div className={classes.headerContainer}>
        <div
          className={classes.backContainer}
          onClick={() => {
            navigate(-1);
          }}
        >
          <ArrowBackIosIcon />
          Back
        </div>
        <Box className={classes.alignButton}></Box>
      </div>
      <Container className={classes.container}>
        <Box className={classes.innerContainer}>
          <Box className={classes.header}>
            <Typography variant="h6" component="div">
              Upstream Leased Asset
            </Typography>
          </Box>
          <Box className={classes.topContainer}>
            <Grid
              container
              direction={"row"}
              wrap="nowrap"
              justifyContent={"space-between"}
              display={"contents"}
              spacing={12}
            >
              <Grid
                item
                container
                direction={"column"}
                display={"contents"}
                xs={10}
              >
                <CeroItemPair
                  title="Facility:"
                  value={
                    emissionData.facility_name
                      ? emissionData.facility_name
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />

                <CeroItemPair
                  title="Month and Year:"
                  value={
                    emissionData.year && emissionData.month
                      ? monthDict[emissionData.month] + " " + emissionData.year
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                <CeroItemPair
                  title="Calculation Method:"
                  value={
                    emissionData.calculation_method
                      ? emissionData.calculation_method.includes("Average")
                        ? `${emissionData.calculation_method} - ${emissionData.type_of_average_data_method_name}`
                        : emissionData.calculation_method
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                {emissionData?.calculation_method?.toLowerCase() ===
                "asset-specific method" ? (
                  <>
                    <CeroItemPair
                      title="Fuel Source:"
                      value={
                        emissionData.fuel_source
                          ? emissionData.fuel_source
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Amount of Fuel:"
                      value={
                        emissionData.fuel_amount
                          ? emissionData.fuel_amount
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />

                    <CeroItemPair
                      title="Fuel unit:"
                      value={
                        emissionData.fuel_unit ? emissionData.fuel_unit : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Gas type:"
                      value={
                        emissionData.gas_type_name
                          ? emissionData.gas_type_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Refrigerant:"
                      value={
                        emissionData.refrigerant_name
                          ? emissionData.refrigerant_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Quantity of refrigerant leakage:"
                      value={
                        emissionData.amount_refrigerants_leakage
                          ? emissionData.amount_refrigerants_leakage
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Unit:"
                      value={
                        emissionData.refrigerant_unit
                          ? emissionData.refrigerant_unit
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Calculation approach:"
                      value={
                        emissionData.electricity_calculation_approach
                          ? emissionData.electricity_calculation_approach
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Quantity of electricity, steam, heating, cooling consumed:"
                      value={
                        emissionData.amount_electricity
                          ? emissionData.amount_electricity
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Unit:"
                      value={
                        emissionData.electricity_unit
                          ? emissionData.electricity_unit
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Process of emissions:"
                      value={
                        emissionData.process_emission
                          ? emissionData.process_emission
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                ) : emissionData?.calculation_method?.toLowerCase() ===
                  "lessor-specific method" ? (
                  <>
                    <CeroItemPair
                      title="Fuel Source:"
                      value={
                        emissionData.fuel_source
                          ? emissionData.fuel_source
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Amount of Fuel:"
                      value={
                        emissionData.fuel_amount
                          ? emissionData.fuel_amount
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />

                    <CeroItemPair
                      title="Fuel unit:"
                      value={
                        emissionData.fuel_unit ? emissionData.fuel_unit : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Gas type:"
                      value={
                        emissionData.gas_type_name
                          ? emissionData.gas_type_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Refrigerant:"
                      value={
                        emissionData.refrigerant_name
                          ? emissionData.refrigerant_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Quantity of refrigerant leakage:"
                      value={
                        emissionData.amount_refrigerants_leakage
                          ? emissionData.amount_refrigerants_leakage
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Unit:"
                      value={
                        emissionData.refrigerant_unit
                          ? emissionData.refrigerant_unit
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Calculation approach:"
                      value={
                        emissionData.electricity_calculation_approach
                          ? emissionData.electricity_calculation_approach
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Quantity of electricity, steam, heating, cooling consumed:"
                      value={
                        emissionData.amount_electricity
                          ? emissionData.amount_electricity
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Unit:"
                      value={
                        emissionData.electricity_unit
                          ? emissionData.electricity_unit
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Process of emissions:"
                      value={
                        emissionData.process_emission
                          ? emissionData.process_emission
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Area, volume, quantity of leased asset:"
                      value={
                        emissionData.amount_of_leased_assets
                          ? emissionData.amount_of_leased_assets
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Total area, volume, quantity of lessor assets:"
                      value={
                        emissionData.total_amount_of_leased_assets
                          ? emissionData.total_amount_of_leased_assets
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                ) : emissionData?.calculation_method?.toLowerCase() ===
                  "average-data method" ? (
                  emissionData?.type_of_average_data_method_name?.toLowerCase() ===
                  "type1 - floor space data is available" ? (
                    <>
                      <CeroItemPair
                        title="Building type:"
                        value={
                          emissionData.building_type_name
                            ? emissionData.building_type_name
                            : "NA"
                        }
                        classes={{
                          container: classes.infoContainer,
                          title: classes.diversityTitle,
                          value: classes.value,
                        }}
                      />
                      <CeroItemPair
                        title="Total floor space of building type:"
                        value={
                          emissionData.total_floor_space
                            ? emissionData.total_floor_space
                            : "NA"
                        }
                        classes={{
                          container: classes.infoContainer,
                          title: classes.diversityTitle,
                          value: classes.value,
                        }}
                      />
                      <CeroItemPair
                        title="Unit:"
                        value={
                          emissionData.building_type_unit
                            ? emissionData.building_type_unit
                            : "NA"
                        }
                        classes={{
                          container: classes.infoContainer,
                          title: classes.diversityTitle,
                          value: classes.value,
                        }}
                      />
                    </>
                  ) : emissionData?.type_of_average_data_method_name?.toLowerCase() ===
                    "type2 - floor space data is unavailable" ? (
                    <>
                      <CeroItemPair
                        title="Number of assets:"
                        value={
                          emissionData.number_of_assets
                            ? emissionData.number_of_assets
                            : "NA"
                        }
                        classes={{
                          container: classes.infoContainer,
                          title: classes.diversityTitle,
                          value: classes.value,
                        }}
                      />
                      <CeroItemPair
                        title="Average emission factor for asset type (kgCo2e/m2/year):"
                        value={
                          emissionData.average_emission_factor_of_building_type
                            ? emissionData.average_emission_factor_of_building_type
                            : "NA"
                        }
                        classes={{
                          container: classes.infoContainer,
                          title: classes.diversityTitle,
                          value: classes.value,
                        }}
                      />
                    </>
                  ) : (
                    <></>
                  )
                ) : (
                  <></>
                )}
                {emissionData?.audited_by && (
                  <>
                    <CeroItemPair
                      title="Audited by:"
                      value={
                        emissionData.audited_by ? emissionData.audited_by : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Audited on:"
                      value={
                        emissionData.audited_on
                          ? dayjs(emissionData.audited_on).format("DD/MM/YYYY")
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Audited on:"
                      value={
                        emissionData.audited_on
                          ? dayjs(emissionData.audited_on).format("DD/MM/YYYY")
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Audited on:"
                      value={
                        emissionData.audited_on
                          ? dayjs(emissionData.audited_on).format("DD/MM/YYYY")
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.bottomContainer}>
            <Typography
              variant="h6"
              component="h6"
              className={classes.previewTitle}
            >
              Emission
            </Typography>

            <Grid container direction="row" spacing={4}>
              {/* LEFT COLUMN */}
              <Grid item container direction="column" xs={12} md={6}>
                {emissionData?.co2 != null && (
                  <Typography className={classes.previewItem}>
                    CO<sub>2</sub>: {emissionData.co2} tonnes
                  </Typography>
                )}
                {emissionData?.ch4 != null && (
                  <Typography className={classes.previewItem}>
                    CH<sub>4</sub>: {emissionData.ch4} tonnes
                  </Typography>
                )}
                {emissionData?.biofuel_co2 != null && (
                  <Typography className={classes.previewItem}>
                    BioFuel CO<sub>2</sub>: {emissionData.biofuel_co2} tonnes
                  </Typography>
                )}
                {emissionData?.co2e != null && (
                  <Typography className={classes.previewItem}>
                    CO<sub>2</sub>e: {emissionData.co2e} tonnes
                  </Typography>
                )}
              </Grid>

              {/* RIGHT COLUMN */}
              <Grid item container direction="column" xs={12} md={6}>
                {emissionData?.n2o != null && (
                  <Typography className={classes.previewItem}>
                    N<sub>2</sub>O: {emissionData.n2o} tonnes
                  </Typography>
                )}
                {emissionData?.ef != null && (
                  <Typography className={classes.previewItem}>
                    EF: {emissionData.ef} kgCO<sub>2</sub>e/unit
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default UpstreamLeasedAsset;
